import { Group, Radio, SimpleGrid, Stack, Text } from '@mantine/core';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  TravelRegistryType,
  getTravelRegistryType,
  travelActivityTypeList,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { FormControlInput } from '@/core/components';

export interface TravelActivityTypeCardsProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;
  description?: string;

  isDisabled?: boolean;
  // The series a request already holds a registry number in, which it can no longer leave.
  lockedRegistryType?: TravelRegistryType | null;
}

export function TravelActivityTypeCards<T extends FieldValues>(props: TravelActivityTypeCardsProps<T>) {
  const { withAsterisk, label, description, name, isSubmitting, isDisabled, control } = props;
  const { lockedRegistryType = null } = props;
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const lockReasonId = `${field.name}-registry-lock`;

  return (
    <Stack gap={4}>
      <Radio.Group
        ref={field.ref}
        name={field.name}
        value={field.value}
        onChange={field.onChange}
        label={label}
        description={description}
        withAsterisk={withAsterisk}
        error={error?.message}
      >
        <SimpleGrid
          cols={{ base: 1, sm: 2, lg: 3 }}
          mt="xs"
        >
          {travelActivityTypeList.map(activityType => {
            const locked = lockedRegistryType != null && getTravelRegistryType(activityType) !== lockedRegistryType;
            const descriptionId = `${field.name}-${activityType}-description`;
            // Mantine's Radio.Card has no disabled look of its own, so a card that cannot be chosen is dimmed here.
            const unavailable = isDisabled || locked;

            return (
              <Radio.Card
                key={activityType}
                value={activityType}
                p="sm"
                bg={unavailable ? 'gray.1' : 'white'}
                opacity={unavailable ? 0.55 : undefined}
                style={unavailable ? { cursor: 'not-allowed' } : undefined}
                disabled={isSubmitting || isDisabled || locked}
                // The card label alone is the accessible name; the sentence below it is a description, not part of it.
                aria-labelledby={`${field.name}-${activityType}-label`}
                aria-describedby={locked ? `${descriptionId} ${lockReasonId}` : descriptionId}
              >
                <Group
                  align="flex-start"
                  wrap="nowrap"
                >
                  <Radio.Indicator disabled={unavailable} />
                  <Stack gap="2px">
                    <Text
                      id={`${field.name}-${activityType}-label`}
                      size="sm"
                      fw={500}
                    >
                      {t(`travel.activityTypeEnum.${activityType}`)}
                    </Text>
                    <Text
                      id={descriptionId}
                      size="xs"
                      c="dimmed"
                    >
                      {t(`travel.activityTypeDescription.${activityType}`)}
                    </Text>
                  </Stack>
                </Group>
              </Radio.Card>
            );
          })}
        </SimpleGrid>
      </Radio.Group>
      {lockedRegistryType != null && (
        <Text
          id={lockReasonId}
          size="xs"
          c="dimmed"
        >
          {t('travel.requests.form.registryTypeLocked', {
            registry: t(`travel.registryTypeEnum.${lockedRegistryType}`),
          })}
        </Text>
      )}
    </Stack>
  );
}
